/**
 * Browser IANA timezone catalog + friendly labels for the Settings selector.
 * The selectable list comes from Intl.supportedValuesOf("timeZone") when available.
 * Saved values remain canonical IANA identifiers (e.g. Asia/Kolkata).
 */

const FALLBACK_TIMEZONES = [
  "UTC",
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Africa/Lagos",
  "Africa/Nairobi",
  "America/Bogota",
  "America/Argentina/Buenos_Aires",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Mexico_City",
  "America/New_York",
  "America/Sao_Paulo",
  "America/Toronto",
  "America/Vancouver",
  "Asia/Dubai",
  "Asia/Hong_Kong",
  "Asia/Kolkata",
  "Asia/Seoul",
  "Asia/Shanghai",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Perth",
  "Australia/Sydney",
  "Europe/Amsterdam",
  "Europe/Berlin",
  "Europe/London",
  "Europe/Madrid",
  "Europe/Paris",
  "Europe/Rome",
  "Pacific/Auckland",
  "Pacific/Honolulu",
];

/**
 * Deprecated names this runtime still emits via Intl.supportedValuesOf("timeZone").
 * Keep this list small and only well-established IANA links.
 */
const CANONICAL_IANA = {
  "Asia/Calcutta": "Asia/Kolkata",
  "Asia/Katmandu": "Asia/Kathmandu",
  "Asia/Saigon": "Asia/Ho_Chi_Minh",
  "Asia/Rangoon": "Asia/Yangon",
  "Europe/Kiev": "Europe/Kyiv",
  "America/Buenos_Aires": "America/Argentina/Buenos_Aires",
  "America/Indianapolis": "America/Indiana/Indianapolis",
  "America/Louisville": "America/Kentucky/Louisville",
  "America/Godthab": "America/Nuuk",
};

/** Extra search tokens so city aliases still match (e.g. kolkata ↔ Calcutta). */
const SEARCH_EXTRA = {
  "Asia/Kolkata": ["kolkata", "calcutta", "india"],
  "Asia/Calcutta": ["kolkata", "calcutta", "india"],
};

/** Display-only country overlay for common zones — not a second timezone authority. */
const COUNTRY_BY_IANA = {
  "Africa/Cairo": "Egypt",
  "Africa/Johannesburg": "South Africa",
  "Africa/Lagos": "Nigeria",
  "Africa/Nairobi": "Kenya",
  "America/Anchorage": "United States",
  "America/Argentina/Buenos_Aires": "Argentina",
  "America/Bogota": "Colombia",
  "America/Chicago": "United States",
  "America/Denver": "United States",
  "America/Detroit": "United States",
  "America/Edmonton": "Canada",
  "America/Halifax": "Canada",
  "America/Indiana/Indianapolis": "United States",
  "America/Kentucky/Louisville": "United States",
  "America/Los_Angeles": "United States",
  "America/Mexico_City": "Mexico",
  "America/New_York": "United States",
  "America/Nuuk": "Greenland",
  "America/Phoenix": "United States",
  "America/Santiago": "Chile",
  "America/Sao_Paulo": "Brazil",
  "America/Toronto": "Canada",
  "America/Vancouver": "Canada",
  "America/Winnipeg": "Canada",
  "Asia/Bangkok": "Thailand",
  "Asia/Dubai": "United Arab Emirates",
  "Asia/Hong_Kong": "Hong Kong",
  "Asia/Ho_Chi_Minh": "Vietnam",
  "Asia/Jakarta": "Indonesia",
  "Asia/Kathmandu": "Nepal",
  "Asia/Kolkata": "India",
  "Asia/Seoul": "South Korea",
  "Asia/Shanghai": "China",
  "Asia/Singapore": "Singapore",
  "Asia/Tokyo": "Japan",
  "Asia/Yangon": "Myanmar",
  "Atlantic/Reykjavik": "Iceland",
  "Australia/Adelaide": "Australia",
  "Australia/Brisbane": "Australia",
  "Australia/Melbourne": "Australia",
  "Australia/Perth": "Australia",
  "Australia/Sydney": "Australia",
  "Europe/Amsterdam": "Netherlands",
  "Europe/Athens": "Greece",
  "Europe/Berlin": "Germany",
  "Europe/Brussels": "Belgium",
  "Europe/Dublin": "Ireland",
  "Europe/Kyiv": "Ukraine",
  "Europe/Lisbon": "Portugal",
  "Europe/London": "United Kingdom",
  "Europe/Madrid": "Spain",
  "Europe/Moscow": "Russia",
  "Europe/Paris": "France",
  "Europe/Rome": "Italy",
  "Europe/Stockholm": "Sweden",
  "Europe/Vienna": "Austria",
  "Europe/Warsaw": "Poland",
  "Europe/Zurich": "Switzerland",
  "Pacific/Auckland": "New Zealand",
  "Pacific/Honolulu": "United States",
};

/** Prefix rules that are geographically unambiguous. */
const PREFIX_COUNTRY = [
  ["America/Argentina/", "Argentina"],
  ["America/Indiana/", "United States"],
  ["America/Kentucky/", "United States"],
  ["America/North_Dakota/", "United States"],
  ["Australia/", "Australia"],
  ["Brazil/", "Brazil"],
  ["Canada/", "Canada"],
  ["Chile/", "Chile"],
  ["Mexico/", "Mexico"],
  ["US/", "United States"],
];

export function canonicalizeIana(iana) {
  if (!iana) return "";
  return CANONICAL_IANA[iana] || iana;
}

export function listIanaTimezones() {
  let raw = [];
  try {
    if (typeof Intl !== "undefined" && typeof Intl.supportedValuesOf === "function") {
      const values = Intl.supportedValuesOf("timeZone");
      if (Array.isArray(values) && values.length > 0) {
        raw = values.filter((zone) => typeof zone === "string" && zone.trim());
      }
    }
  } catch {
    raw = [];
  }
  if (!raw.length) raw = FALLBACK_TIMEZONES.slice();

  const seen = new Set();
  const out = [];
  for (const zone of raw) {
    const canonical = canonicalizeIana(zone);
    if (seen.has(canonical)) continue;
    seen.add(canonical);
    out.push(canonical);
  }
  return out;
}

export function cityFromIana(iana) {
  if (!iana) return "";
  if (iana === "UTC" || iana === "Etc/UTC") return "UTC";
  const last = String(iana).split("/").pop() || iana;
  return last.replace(/_/g, " ");
}

export function countryForIana(iana) {
  if (!iana) return "";
  const canonical = canonicalizeIana(iana);
  if (COUNTRY_BY_IANA[canonical]) return COUNTRY_BY_IANA[canonical];
  if (COUNTRY_BY_IANA[iana]) return COUNTRY_BY_IANA[iana];
  for (const [prefix, country] of PREFIX_COUNTRY) {
    if (canonical.startsWith(prefix) || iana.startsWith(prefix)) return country;
  }
  return "";
}

export function normalizeSearch(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[_/,—–-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function pad2(value) {
  return String(value).padStart(2, "0");
}

function formatOffsetMinutes(totalMinutes) {
  if (!Number.isFinite(totalMinutes)) return "";
  const sign = totalMinutes >= 0 ? "+" : "-";
  const abs = Math.abs(totalMinutes);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  return `UTC${sign}${pad2(hours)}:${pad2(minutes)}`;
}

function parseOffsetName(raw) {
  if (!raw) return "";
  const match = String(raw).match(/(?:GMT|UTC)([+-])(\d{1,2})(?::?(\d{2}))?/i);
  if (!match) return "";
  return `UTC${match[1]}${pad2(match[2])}:${pad2(match[3] || "00")}`;
}

function readOffsetName(iana, at) {
  for (const timeZoneName of ["longOffset", "shortOffset"]) {
    try {
      const part = new Intl.DateTimeFormat("en-US", {
        timeZone: iana,
        timeZoneName,
        hour: "2-digit",
      })
        .formatToParts(at)
        .find((item) => item.type === "timeZoneName");
      if (part?.value) return part.value;
    } catch {
      // Try the next style.
    }
  }
  return "";
}

function offsetMinutesForZone(iana, at) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: iana,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).formatToParts(at);
    const read = (type) => parts.find((item) => item.type === type)?.value;
    let hour = Number(read("hour"));
    if (hour === 24) hour = 0;
    const asUtc = Date.UTC(
      Number(read("year")),
      Number(read("month")) - 1,
      Number(read("day")),
      hour,
      Number(read("minute")),
      Number(read("second")),
    );
    return Math.round((asUtc - at.getTime()) / 60000);
  } catch {
    return null;
  }
}

export function formatUtcOffset(iana, at = new Date()) {
  if (!iana) return "";
  const parsed = parseOffsetName(readOffsetName(iana, at));
  if (parsed) return parsed;
  return formatOffsetMinutes(offsetMinutesForZone(iana, at));
}

export function formatPrimaryLabel(iana) {
  if (!iana) return "";
  const city = cityFromIana(iana);
  const country = countryForIana(iana);
  if (iana === "UTC" || iana === "Etc/UTC") return "UTC";
  return country ? `${city}, ${country}` : city;
}

export function formatSecondaryLabel(iana, at = new Date()) {
  if (!iana) return "";
  const offset = formatUtcOffset(iana, at);
  return offset ? `${iana} · ${offset}` : iana;
}

export function formatTimezoneLabel(iana) {
  return formatPrimaryLabel(iana);
}

export function buildTimezoneOption(iana, at = new Date()) {
  const city = cityFromIana(iana);
  const country = countryForIana(iana);
  const region = iana.includes("/") ? iana.split("/")[0] : iana;
  const primaryLabel = formatPrimaryLabel(iana);
  const offset = formatUtcOffset(iana, at);
  const secondaryLabel = formatSecondaryLabel(iana, at);
  const extra = SEARCH_EXTRA[iana] || SEARCH_EXTRA[canonicalizeIana(iana)] || [];
  const searchHaystack = normalizeSearch(
    [iana, iana.replace(/_/g, " "), city, country, region, primaryLabel, secondaryLabel, ...extra].join(" "),
  );
  return {
    value: iana,
    city,
    country,
    region,
    offset,
    primaryLabel,
    secondaryLabel,
    label: primaryLabel,
    searchHaystack,
  };
}

export function getTimezoneOptions(at = new Date()) {
  const seen = new Set();
  const options = [];
  for (const zone of listIanaTimezones()) {
    if (seen.has(zone)) continue;
    seen.add(zone);
    options.push(buildTimezoneOption(zone, at));
  }
  return options;
}

export function filterTimezoneOptions(options, query) {
  const needle = normalizeSearch(query);
  if (!needle) return options;
  return options.filter((option) => option.searchHaystack.includes(needle));
}
